import React, { FC, useMemo, useState, useCallback } from "react";
import { Card, CardContent, Typography, Box, IconButton } from "@mui/material";
import { TableChartOutlined, ChevronRight } from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import { formatNumberMetric } from "../../utils/commonUtils";

interface IInterceptorDataItem {
	id: number;
	numberplate: string;
	location: string;
	approved: number;
	pending: number;
	rejected: number;
	total: number;
}

interface IRegionDataItem {
	id: number;
	region: string;
	approved: number;
	pending: number;
	rejected: number;
	total: number;
	interceptors: IInterceptorDataItem[];
}

interface IRegionalSummaryTableProps {
	data: IRegionDataItem[];
	inactiveData?: IRegionDataItem[];
}

interface ITableRow {
	id: string;
	name: string;
	approved: number;
	pending: number;
	rejected: number;
	total: number;
	isRegion: boolean;
	regionId: string;
	isSeparator?: boolean;
	isInactive?: boolean;
}

const INACTIVE_SEPARATOR_ID = "inactive-regions-separator";

const RegionalSummaryTable: FC<IRegionalSummaryTableProps> = ({ data, inactiveData = [] }) => {
	const [expandedRegionId, setExpandedRegionId] = useState<string | null>(null);
	const [expandedInactiveRegionId, setExpandedInactiveRegionId] = useState<string | null>(null);

	const hasData = Array.isArray(data) && data.length > 0;
	const hasInactiveData = Array.isArray(inactiveData) && inactiveData.length > 0;

	const allRegionRows = useMemo<ITableRow[]>(() => {
		if (!hasData) return [];
		return [...data]
			.sort((a, b) => b.total - a.total)
			.map((region) => ({
				id: `region-${region.id}`,
				name: region.region,
				approved: region.approved,
				pending: region.pending,
				rejected: region.rejected,
				total: region.total,
				isRegion: true,
				regionId: `region-${region.id}`
			}));
	}, [hasData, data]);

	const interceptorRowsByRegion = useMemo<Record<string, ITableRow[]>>(() => {
		if (!hasData) return {};
		const map: Record<string, ITableRow[]> = {};
		data.forEach((region) => {
			const regionId = `region-${region.id}`;
			if (region.interceptors && region.interceptors.length > 0) {
				map[regionId] = region.interceptors.map((interceptor) => ({
					id: `interceptor-${interceptor.id}`,
					name: `${interceptor.numberplate} - ${interceptor.location}`,
					approved: interceptor.approved,
					pending: interceptor.pending,
					rejected: interceptor.rejected,
					total: interceptor.total,
					isRegion: false,
					regionId
				}));
			} else {
				map[regionId] = [];
			}
		});
		return map;
	}, [hasData, data]);

	const allInactiveRegionRows = useMemo<ITableRow[]>(() => {
		if (!hasInactiveData) return [];
		return [...inactiveData]
			.sort((a, b) => b.total - a.total)
			.map((region) => ({
				id: `inactive-region-${region.id}`,
				name: region.region,
				approved: region.approved,
				pending: region.pending,
				rejected: region.rejected,
				total: region.total,
				isRegion: true,
				regionId: `inactive-region-${region.id}`,
				isInactive: true
			}));
	}, [hasInactiveData, inactiveData]);

	const interceptorRowsByInactiveRegion = useMemo<Record<string, ITableRow[]>>(() => {
		if (!hasInactiveData) return {};
		const map: Record<string, ITableRow[]> = {};
		inactiveData.forEach((region) => {
			const regionId = `inactive-region-${region.id}`;
			if (region.interceptors && region.interceptors.length > 0) {
				map[regionId] = region.interceptors.map((interceptor) => ({
					id: `inactive-interceptor-${interceptor.id}`,
					name: `${interceptor.numberplate} - ${interceptor.location}`,
					approved: interceptor.approved,
					pending: interceptor.pending,
					rejected: interceptor.rejected,
					total: interceptor.total,
					isRegion: false,
					regionId,
					isInactive: true
				}));
			} else {
				map[regionId] = [];
			}
		});
		return map;
	}, [hasInactiveData, inactiveData]);

	const visibleRows = useMemo<ITableRow[]>(() => {
		const rows: ITableRow[] = [];
		allRegionRows.forEach((regionRow) => {
			rows.push(regionRow);
			if (expandedRegionId === regionRow.regionId) {
				const children = interceptorRowsByRegion[regionRow.regionId] ?? [];
				rows.push(...children);
			}
		});
		if (hasInactiveData) {
			rows.push({
				id: INACTIVE_SEPARATOR_ID,
				name: "Inactive Regions",
				approved: 0,
				pending: 0,
				rejected: 0,
				total: 0,
				isRegion: false,
				regionId: INACTIVE_SEPARATOR_ID,
				isSeparator: true
			});
			allInactiveRegionRows.forEach((regionRow) => {
				rows.push(regionRow);
				if (expandedInactiveRegionId === regionRow.regionId) {
					const children = interceptorRowsByInactiveRegion[regionRow.regionId] ?? [];
					rows.push(...children);
				}
			});
		}
		return rows;
	}, [
		allRegionRows,
		interceptorRowsByRegion,
		expandedRegionId,
		hasInactiveData,
		allInactiveRegionRows,
		interceptorRowsByInactiveRegion,
		expandedInactiveRegionId
	]);

	const handleToggleExpand = useCallback((regionId: string) => {
		setExpandedRegionId((prev) => (prev === regionId ? null : regionId));
	}, []);

	const handleToggleExpandInactive = useCallback((regionId: string) => {
		setExpandedInactiveRegionId((prev) => (prev === regionId ? null : regionId));
	}, []);

	const columns = useMemo<GridColDef[]>(
		() => [
			{
				field: "name",
				headerName: "Region / Interceptor",
				flex: 1,
				minWidth: 250,
				sortable: false,
				renderCell: (params) => {
					const row = params.row as ITableRow;
					if (row.isSeparator) {
						return (
							<Box
								className="separator-cell"
								sx={{
									display: "flex",
									alignItems: "center",
									width: "100%",
									height: "100%",
									bgcolor: "grey.200",
									// borderLeft: "3px solid",
									borderColor: "grey.400",
									fontWeight: 700,
									color: "text.secondary"
								}}
							>
								<Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary" }}>
									{row.name}
								</Typography>
							</Box>
						);
					}
					if (row.isRegion) {
						const isInactive = row.isInactive === true;
						const isExpanded = isInactive
							? expandedInactiveRegionId === row.regionId
							: expandedRegionId === row.regionId;
						const interceptorCount = isInactive
							? interceptorRowsByInactiveRegion[row.regionId]?.length ?? 0
							: interceptorRowsByRegion[row.regionId]?.length ?? 0;
						const hasChildren = interceptorCount > 0;
						const onToggle = isInactive ? handleToggleExpandInactive : handleToggleExpand;
						return (
							<Box
								className="region-cell"
								sx={{
									display: "flex",
									alignItems: "center",
									width: "100%",
									height: "100%"
								}}
							>
								{hasChildren ? (
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											onToggle(row.regionId);
										}}
										sx={{
											p: 0.25,
											mr: 0.5,
											"& svg": {
												transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
												transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)"
											}
										}}
										aria-label={isExpanded ? "Collapse" : "Expand"}
									>
										<ChevronRight />
									</IconButton>
								) : (
									<Box sx={{ width: 28 }} />
								)}
								<Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
									{row.name}
									{interceptorCount > 0 && (
										<Box
											component="span"
											sx={{
												fontWeight: 500,
												color: "text.secondary",
												ml: 0.5
											}}
										>
											({formatNumberMetric(interceptorCount)})
										</Box>
									)}
								</Typography>
							</Box>
						);
					}
					return (
						<Box
							className="interceptor-cell"
							sx={{
								pl: 5,
								display: "flex",
								alignItems: "center",
								width: "100%",
								height: "100%"
							}}
						>
							<Typography variant="body2" sx={{ color: "text.primary" }}>
								{row.name}
							</Typography>
						</Box>
					);
				}
			},
			{
				field: "approved",
				headerName: "Issued",
				width: 150,
				headerAlign: "right",
				align: "right",
				type: "number",
				sortable: false,
				renderCell: (params) => {
					const row = params.row as ITableRow;
					if (row.isSeparator)
						return (
							<Box sx={{ justifyContent: "flex-end", display: "flex", width: "100%" }}>
								<Typography variant="body2" sx={{ color: "text.secondary" }}></Typography>
							</Box>
						);
					const value = row.approved ?? params.value ?? (row.isInactive ? 0 : "");
					return (
						<Box
							sx={{ justifyContent: "flex-end", display: "flex", width: "100%", height: "100%", alignItems: "center" }}
						>
							<Typography variant="body2" sx={{ color: "text.primary" }}>
								{formatNumberMetric(value)}
							</Typography>
						</Box>
					);
				}
			},
			{
				field: "pending",
				headerName: "Pending",
				width: 150,
				headerAlign: "right",
				align: "right",
				type: "number",
				sortable: false,
				renderCell: (params) => {
					const row = params.row as ITableRow;
					if (row.isSeparator)
						return (
							<Box sx={{ justifyContent: "flex-end", display: "flex", width: "100%" }}>
								<Typography variant="body2" sx={{ color: "text.secondary" }}></Typography>
							</Box>
						);
					const value = row.pending ?? params.value ?? (row.isInactive ? 0 : "");
					return (
						<Box
							sx={{ justifyContent: "flex-end", display: "flex", width: "100%", height: "100%", alignItems: "center" }}
						>
							<Typography variant="body2" sx={{ color: "text.primary" }}>
								{formatNumberMetric(value)}
							</Typography>
						</Box>
					);
				}
			},
			{
				field: "rejected",
				headerName: "Rejected",
				width: 150,
				headerAlign: "right",
				align: "right",
				type: "number",
				sortable: false,
				renderCell: (params) => {
					const row = params.row as ITableRow;
					if (row.isSeparator)
						return (
							<Box sx={{ justifyContent: "flex-end", display: "flex", width: "100%" }}>
								<Typography variant="body2" sx={{ color: "text.secondary" }}></Typography>
							</Box>
						);
					const value = row.rejected ?? params.value ?? (row.isInactive ? 0 : "");
					return (
						<Box sx={{ justifyContent: "flex-end", display: "flex", width: "100%", height: "100%", alignItems: "center" }}>
							<Typography variant="body2" sx={{ color: "text.primary" }}>
								{formatNumberMetric(value)}
							</Typography>
						</Box>
					);
				}
			},
			{
				field: "total",
				headerName: "Total",
				width: 150,
				headerAlign: "right",
				align: "right",
				type: "number",
				sortable: false,
				renderCell: (params) => {
					const row = params.row as ITableRow;
					if (row.isSeparator)
						return (
							<Box sx={{ justifyContent: "flex-end", display: "flex", width: "100%" }}>
								<Typography variant="body2" sx={{ color: "text.secondary" }}></Typography>
							</Box>
						);
					const value = row.total ?? params.value ?? (row.isInactive ? 0 : "");
					return (
						<Box sx={{ justifyContent: "flex-end", display: "flex", width: "100%", height: "100%", alignItems: "center" }}>
							<Typography variant="body2" sx={{ color: "text.primary" }}>
								{formatNumberMetric(value)}
							</Typography>
						</Box>
					);
				}
			},
			{
				field: "approvalRate",
				headerName: "Approval Rate",
				width: 200,
				headerAlign: "right",
				align: "right",
				sortable: false,
				renderCell: (params) => {
					const row = params.row as ITableRow;
					if (row.isSeparator) {
						return (
							<Box className="align-center" sx={{ justifyContent: "flex-end" }}>
								<Typography variant="body2" sx={{ color: "text.secondary" }}></Typography>
							</Box>
						);
					}
					const total = row.total ?? 0;
					const approved = row.approved ?? 0;
					const rate = total > 0 ? (approved / total) * 100 : 0;
					const color = rate >= 50 ? "#059669" : "#d97706";
					return (
						<Box className="align-center" sx={{ justifyContent: "flex-end" }}>
							<Typography
								variant="body2"
								sx={{
									fontWeight: 500,
									color: color
								}}
							>
								{formatNumberMetric(rate, 1)}%
							</Typography>
						</Box>
					);
				}
			}
		],
		[
			expandedRegionId,
			expandedInactiveRegionId,
			interceptorRowsByRegion,
			interceptorRowsByInactiveRegion,
			handleToggleExpand,
			handleToggleExpandInactive
		]
	);

	return (
		<Card
			sx={{
				bgcolor: "background.paper",
				border: "1px solid rgba(0, 0, 0, 0.08)",
				borderRadius: 3,
				boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
			}}
		>
			<CardContent sx={{ p: 3, pb: 2, "&:last-child": { pb: 2 } }}>
				<Typography variant="h6" fontWeight={600} sx={{ color: "text.primary", mb: 2 }}>
					Regional Summary
				</Typography>
				{hasData || hasInactiveData ? (
					<Box sx={{ width: "100%" }}>
						<DataGrid
							rows={visibleRows}
							columns={columns}
							disableRowSelectionOnClick
							disableColumnMenu
							hideFooter
							rowHeight={42}
							columnHeaderHeight={56}
							getRowClassName={(params) => {
								const row = params.row as ITableRow;
								if (row.isSeparator) return "separator-row";
								if (row.isInactive && row.isRegion) return "region-row inactive-region-row";
								if (row.isInactive) return "child-row-animated inactive-child-row";
								return row.isRegion ? "region-row" : "child-row-animated";
							}}
							sx={{
								"& .MuiDataGrid-virtualScroller": {
									"& .MuiDataGrid-virtualScrollerContent": {
										"& .MuiDataGrid-virtualScrollerRenderZone": {
											"& .child-row-animated": {
												opacity: 0,
												animation: "fadeSlideIn 0.3s ease-out forwards"
											}
										}
									}
								},
								"& .MuiDataGrid-columnHeaders": {
									bgcolor: "transparent"
								},
								"& .MuiDataGrid-columnHeader": {
									fontWeight: 600,
									color: "text.primary",
									"&:focus": { outline: "none" },
									"&:focus-within": { outline: "none" }
								},
								"& .MuiDataGrid-columnHeaders .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader": {
									borderBottom: "2px solid",
									borderColor: "divider"
								},
								"& .MuiDataGrid-cell": {
									borderBottom: "1px solid",
									borderColor: "rgba(0, 0, 0, 0.08)",
									color: "text.primary",
									fontWeight: 500,
									transition: "background-color 0.2s ease-in-out",
									"&:focus": { outline: "none" },
									"&:focus-within": { outline: "none" }
								},
								"& .MuiDataGrid-row": {
									transition: "background-color 0.2s ease-in-out, opacity 0.2s ease-in-out",
									"&:hover": { bgcolor: "action.hover" }
								},
								"& .MuiDataGrid-row.region-row .MuiDataGrid-cell:first-of-type": {
									fontWeight: 600
								},
								"& .MuiDataGrid-row.child-row-animated": {
									bgcolor: "action.hover",
									fontSize: "0.875rem",
									"& .MuiDataGrid-cell": { color: "text.primary" }
								},
								"& .MuiDataGrid-row.separator-row .MuiDataGrid-cell": {
									bgcolor: "grey.200",
									borderBottom: "2px solid",
									borderColor: "divider",
									fontWeight: 700
								},
								"& .MuiDataGrid-row.separator-row .MuiDataGrid-cell:first-of-type": {
									borderLeft: "3px solid",
									borderColor: "grey.400"
								},
								"& .MuiDataGrid-row.inactive-region-row .MuiDataGrid-cell": {
									bgcolor: "grey.50"
								},
								"& .MuiDataGrid-row.inactive-child-row .MuiDataGrid-cell": {
									bgcolor: "grey.50"
								},
								"& .MuiDataGrid-columnSeparator": { display: "none" },
								"@keyframes fadeSlideIn": {
									"0%": {
										opacity: 0,
										transform: "translateY(-8px)"
									},
									"100%": {
										opacity: 1,
										transform: "translateY(0)"
									}
								}
							}}
						/>
					</Box>
				) : (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							py: 6
						}}
					>
						<TableChartOutlined sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
						<Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
							No Data Available
						</Typography>
						<Typography variant="body2" color="text.disabled">
							No regional summary data found for the selected filters
						</Typography>
					</Box>
				)}
			</CardContent>
		</Card>
	);
};

export default RegionalSummaryTable;
